import React, { useState } from 'react';
import { t } from 'i18next';
import { Skeleton } from 'primereact/skeleton';
import { Dropdown } from 'primereact/dropdown';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import ModelTooltips from '../../../../shared/components/model-tooltips/ModelTooltips';
import { PageTrafficMode } from '../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import { useGetPageTraffic } from '../../../../shared/api/hooks/monitor/data/sources/UseGetPageTraffic';
import PageTrafficTable from './PageTrafficTable';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';
import './PageTrafficSection.scss';

interface TabDef {
  mode: PageTrafficMode;
  i18nLabel: string;
  i18nTooltip: string;
  icon: string;
}

const TABS: TabDef[] = [
  {
    mode: 'top',
    i18nLabel: 'pageTrafficTabTop',
    i18nTooltip: 'pageTrafficTabTopTooltip',
    icon: 'ico--star',
  },
  {
    mode: 'topGainers',
    i18nLabel: 'pageTrafficTabTopGainers',
    i18nTooltip: 'pageTrafficTabTopGainersTooltip',
    icon: 'ico--chart-up',
  },
  {
    mode: 'topDecliners',
    i18nLabel: 'pageTrafficTabTopDecliners',
    i18nTooltip: 'pageTrafficTabTopDeclinersTooltip',
    icon: 'ico--chart-down',
  },
  {
    mode: 'newlyCited',
    i18nLabel: 'pageTrafficTabNewlyCited',
    i18nTooltip: 'pageTrafficTabNewlyCitedTooltip',
    icon: 'ico--zap',
  },
  {
    mode: 'droppedOff',
    i18nLabel: 'pageTrafficTabDroppedOff',
    i18nTooltip: 'pageTrafficTabDroppedOffTooltip',
    icon: 'ico--logout',
  },
];

interface PageTrafficSectionProps {
  projectId: number;
  chatType: ChatType | null;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterMonitorTypes?: string[];
}

const PageTrafficSection: React.FC<PageTrafficSectionProps> = ({
  projectId,
  chatType,
  from,
  to,
  monitorPromptIds,
  filterMonitorTypes,
}) => {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<PageTrafficMode>('top');

  // One request per tab: the whole ranking (capped server-side, see PAGE_TRAFFIC_MAX_ROWS) without
  // prompts or daily trend — prompts load per row on expand (PageTrafficPrompts), and the table has
  // no sparkline. No pagination means no client-side
  // accumulator to keep in sync with the server-side own-domain scope: when a drop in Domain Share
  // Comparison changes the scope, SourcesPage invalidates this query and the fresh rows replace the
  // old ones.
  const { pageTraffic, isLoading } = useGetPageTraffic(
    projectId,
    mode,
    chatType,
    from,
    to,
    monitorPromptIds,
    filterMonitorTypes
  );

  const rows = pageTraffic?.rows ?? [];
  const totalItems = pageTraffic?.pagination.totalItems ?? 0;

  // An empty result means two very different things: the project has no own domain to scope
  // the table to (nothing we can do without the user), or it has one and simply saw no URL
  // activity. `domains` is what tells them apart — `rows` alone looks identical.
  const hasNoOwnDomain = !!pageTraffic && pageTraffic.domains.length === 0;
  // With several own domains in scope, a bare path is ambiguous between them, so the table
  // shows the host too.
  const showHostInUrl = (pageTraffic?.domains?.length ?? 0) > 1;

  return (
    <div className="stats__panel page-traffic-section">
      {/* Mounts global tooltips on every `.ico--xxx-bck` icon class on the page so the
          visible model icons show their names on hover. The same map drives the overflow
          tooltip in ModelIconsList — see shared/components/model-tooltips. */}
      <ModelTooltips />
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('pageTraffic')}
          <CustomTooltip tooltipText="pageTrafficTooltip" addMargin={false} />
        </div>
      </div>

      {/* Reuses the global `.tabs` / `.tabs__item` styling from shared Tabs.scss so the
          look matches the Settings tabs and the rest of the app. The shared Tabs component
          itself is URL-driven (<Link>) — here we keep the mode as local state, so we render
          buttons with the same class names instead of importing the component directly. */}
      <div className="stats-box">
        {/* Telefon: pięć zakładek nie mieści się w rzędzie — ten sam wybór jako dropdown. */}
        {isMobile ? (
          <Dropdown
            value={mode}
            onChange={(e) => setMode(e.value)}
            options={TABS.map((tab) => ({ label: t(tab.i18nLabel), value: tab.mode }))}
            className="dropdown page-traffic-section__mode-dropdown"
          />
        ) : (
        <div className="tabs page-traffic-section__tabs" role="tablist">
          {TABS.map((tab) => {
            const isActive = tab.mode === mode;
            const tooltipId = `page-traffic-tab-${tab.mode}`;
            return (
              <React.Fragment key={tab.mode}>
                <button
                  type="button"
                  role="tab"
                  id={tooltipId}
                  className={`tabs__item ${isActive ? 'tabs__item--active' : ''}`}
                  aria-selected={isActive}
                  onClick={() => setMode(tab.mode)}
                >
                  <span className={`${tab.icon} ico--xs page-traffic-section__tab-icon`} />
                  {t(tab.i18nLabel)}
                </button>
                <CustomTooltip tooltipText={tab.i18nTooltip} targetRef={tooltipId} />
              </React.Fragment>
            );
          })}
        </div>
        )}

        {isLoading ? (
          // Shared table skeleton — same `skeleton--table` markup as DomainsStatsTable and
          // WatchedPagesSection so the loading state is consistent across the SourcesPage.
          // Kept inside `__body` so the min-height reservation prevents a scroll snap on tab
          // switch (the standalone skeleton is shorter than the loaded table).
          <div className="page-traffic-section__body">
            <Skeleton className="skeleton--table skeleton--table-5">
              <span />
              <span />
              <span />
              <span />
              <span />
            </Skeleton>
          </div>
        ) : rows.length === 0 ? (
          <div className="page-traffic-section__body page-traffic-section__body--empty">
            <EmptyPanel
              textKey={hasNoOwnDomain ? 'pageTrafficNoDomainTitle' : 'pageTrafficEmptyTitle'}
              descriptionKey={
                hasNoOwnDomain ? 'pageTrafficNoDomainDescription' : 'pageTrafficEmptyDescription'
              }
            />
          </div>
        ) : (
          // `key={mode}` re-mounts the body on tab switch so the fade-in keyframe replays
          // (without it, React would diff in place and skip the animation).
          <div className="page-traffic-section__body" key={mode}>
            <PageTrafficTable
              rows={rows}
              mode={mode}
              projectId={projectId}
              showHost={showHostInUrl}
              chatType={chatType}
              from={from}
              to={to}
              monitorPromptIds={monitorPromptIds}
              filterMonitorTypes={filterMonitorTypes}
            />
            {totalItems > rows.length && (
              <div className="page-traffic-section__footer page-traffic-section__cap-note">
                {t('pageTrafficShowingTopN', { shown: rows.length, total: totalItems })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageTrafficSection;
